// @flow

import * as React from 'react';
import styles from './VenueCard.module.scss';
import type {
  PreviewVenue,
  HighlightCapacityType
} from 'common/components/venueCardInternalModules/types';
export type {
  PreviewVenue,
  HighlightCapacityType
} from 'common/components/venueCardInternalModules/types';
import VenueInfo from 'common/components/venueCardInternalModules/VenueInfo';
import VenueImage from 'common/components/venueCardInternalModules/VenueImage';
import VenueDescription from 'common/components/venueCardInternalModules/VenueDescription';
import FeaturedVenueIcon from 'search/components/searchResults/FeaturedVenueIcon';
import SuperFeaturedVenueIcon from 'search/components/searchResults/SuperFeaturedVenueIcon';
import impossible from 'common/types/impossible';

export type Props = {|
  venue: PreviewVenue,
  onClickVenueLink?: () => void,
  openInNewTab?: boolean,
  highlightCapacity?: HighlightCapacityType
|};

export function BasicVenueCard(props: Props): React.Node {
  const {
    venue,
    onClickVenueLink,
    openInNewTab = false,
    highlightCapacity
  } = props;

  return (
    <VenueCardTemplate
      venue={venue}
      onClickVenueLink={onClickVenueLink}
      openInNewTab={openInNewTab}
      highlightCapacity={highlightCapacity}
    />
  );
}

type FeaturedVenueCardProps = {|
  ...Props,
  priority: 'REGULAR' | 'HIGH'
|};

export function FeaturedVenueCard(props: FeaturedVenueCardProps): React.Node {
  const { venue, onClickVenueLink, openInNewTab = false, priority } = props;

  const featuredVenueIcon = (() => {
    switch (priority) {
      case 'REGULAR':
        return <FeaturedVenueIcon />;
      case 'HIGH':
        return <SuperFeaturedVenueIcon />;
      default:
        return impossible(priority);
    }
  })();

  return (
    <VenueCardTemplate
      venue={venue}
      onClickVenueLink={onClickVenueLink}
      openInNewTab={openInNewTab}
      featuredVenueIcon={featuredVenueIcon}
    />
  );
}

export type VenueCardWithDescriptionProps = {|
  ...Props,
  description: string
|};

export function VenueCardWithDescription(
  props: VenueCardWithDescriptionProps
): React.Node {
  const { venue, onClickVenueLink, openInNewTab = false, description } = props;

  return (
    <VenueCardTemplate
      venue={venue}
      onClickVenueLink={onClickVenueLink}
      openInNewTab={openInNewTab}
    >
      <VenueDescription description={description} />
    </VenueCardTemplate>
  );
}

type TemplateProps = {|
  ...Props,
  openInNewTab: boolean,
  children?: React.Node,
  featuredVenueIcon?: React.Node,
  highlightCapacity?: HighlightCapacityType
|};

function VenueCardTemplate(props: TemplateProps): React.Node {
  const {
    venue,
    onClickVenueLink,
    openInNewTab,
    children,
    featuredVenueIcon,
    highlightCapacity
  } = props;

  return (
    <div className={styles.venueCard}>
      <VenueImage
        venue={venue}
        onClickVenueLink={onClickVenueLink}
        openInNewTab={openInNewTab}
        featuredVenueIcon={featuredVenueIcon}
      />
      <VenueInfo
        venue={venue}
        onClickVenueLink={onClickVenueLink}
        openInNewTab={openInNewTab}
        highlightCapacity={highlightCapacity}
      >
        {children}
      </VenueInfo>
    </div>
  );
}
